import { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import UnitLinkField from 'components/UnitLinkField/UnitLinkField';
import { SUPPLIER_LINKS } from 'constants/formFields';
import { useUnitRefs } from 'contexts/UnitRefsContext';
import { buildUnitLinkKey, unitRefsToItems } from 'utils/unitRefs';

import type IUnitLinkItem from 'components/UnitLinkField/interfaces/IUnitLinkItem';
import type ISupplierLink from 'store/slices/dataSets/interfaces/ISupplierLink';
import type { IProductUnitLinksField } from '../interfaces/IProductUnitLinksField';

function ProductUnitLinksField({
  control, relationType, profitCentreUID, label, isEditDisabled, boundarySelector,
}: IProductUnitLinksField) {
  const { t } = useTranslation();
  const { getRefs, getRef } = useUnitRefs();

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ control, name: SUPPLIER_LINKS });

  const links = useMemo<ISupplierLink[]>(() => (value as ISupplierLink[]) || [], [value]);

  const refs = useMemo(
    () => getRefs(relationType, profitCentreUID),
    [getRefs, profitCentreUID, relationType],
  );

  const options = useMemo(() => unitRefsToItems(refs), [refs]);

  const linkByItemKey = useMemo(() => new Map(refs.map((ref) => {
    const link = { relationUID: ref.uid, relationType: ref.type };
    return [buildUnitLinkKey(link), link] as const;
  })), [refs]);

  // Only the links this field is responsible for: its type, and — for a profit-centre
  // scoped field — only those whose record belongs to that profit centre. A record in
  // several profit centres therefore shows under each of them off the one stored link.
  const items = useMemo<IUnitLinkItem[]>(() => links.reduce<IUnitLinkItem[]>((acc, link) => {
    if (link.relationType !== relationType) {
      return acc;
    }

    const ref = getRef(relationType, link.relationUID);

    if (profitCentreUID && !ref?.profitCentreUIDs.includes(profitCentreUID)) {
      return acc;
    }

    acc.push({
      itemKey: buildUnitLinkKey(link),
      // Falling back to the uid keeps a link to a missing record visible and removable
      // rather than silently dropping it on save.
      name: ref?.label || link.relationUID,
      type: link.relationType,
    });

    return acc;
  }, []), [getRef, links, profitCentreUID, relationType]);

  const handleToggle = (item: IUnitLinkItem) => {
    const isSelected = links.some((link) => buildUnitLinkKey(link) === item.itemKey);

    if (isSelected) {
      onChange(links.filter((link) => buildUnitLinkKey(link) !== item.itemKey));
      return;
    }

    const link = linkByItemKey.get(item.itemKey);

    if (link) {
      onChange([...links, link]);
    }
  };

  const handleRemove = (itemKey: string) => {
    onChange(links.filter((link) => buildUnitLinkKey(link) !== itemKey));
  };

  return (
    <UnitLinkField
      id={profitCentreUID ? `${SUPPLIER_LINKS}-${profitCentreUID}` : SUPPLIER_LINKS}
      label={label || t('products.fields.suppliedBy')}
      items={items}
      options={options}
      onToggle={handleToggle}
      onRemove={handleRemove}
      isEditDisabled={isEditDisabled}
      error={error?.message}
      boundarySelector={boundarySelector}
    />
  );
}

export default ProductUnitLinksField;
